import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import Close from "../../../../../images/Close.svg";
import { Button } from "../../../../../stories/Button/Button";
import { Label } from "../../../../../stories/Label/Label";
import { Title } from "../../../../../stories/Title/Title";
import { idAndFlagType } from "../../../../../utils/formTypes";
import { emptyIdAndModal, emptyMessage } from "../../../../../utils/constants";
import StarterKit from "@tiptap/starter-kit";
import { useEditor } from "@tiptap/react";
import TextAlign from "@tiptap/extension-text-align";
import Color from "@tiptap/extension-color";
import TextStyle from "@tiptap/extension-text-style";
import BulletList from "@tiptap/extension-bullet-list";
import Document from "@tiptap/extension-document";
import ListItem from "@tiptap/extension-list-item";
import OrderedList from "@tiptap/extension-ordered-list";
import Paragraph from "@tiptap/extension-paragraph";
import Text from "@tiptap/extension-text";
import EditTiptapEditor from "../../../../../styles/EditTipTapEditor";
import { useLazyQuery, useMutation } from "@apollo/client";
import { GetMomDetails, getMomDetailsByNodeId } from "../queries/query";
import {
  AtsMomDetails,
  UpdatePmuCommentsResult,
  UpdatePmuCommentsVars,
  UpdatePmuResponseResult,
  UpdatePmuResponseVars,
  UpdateVendorResponseResult,
  UpdateVendorResponseVars,
} from "../queries/types";
import { nodeVar } from "../../../../../utils/commonQueryTypes";
import { AppContext } from "../../../../../context/context";
import {
  UpdatePmuComments,
  UpdatePmuResponse,
  UpdateVendorResponse,
} from "../queries/mutations";
import { msgType } from "../../../../../utils/types";
import { empType, Operation } from "../../../../../utils/Enum.types";
import useUrlParams from "../../../../../customHooks/useUrlParams";
import useLoggedInUserDetails from "../../../../../customHooks/useLoggedInUserDetails";
import { handleUploadAndDownloadFile } from "../../../../../utils/Upload";
import MessageModal from "../../../../../customComponents/MessageModal";
import { getDownloadUrl } from "../../../../../utils/DownloadFile";
import axios from "axios";
import { GetProjectsForMom } from "../../masters/subsidary/junctions/queries/queries";
import { docTypeEnum } from "../view/PreviousMeetingDetails";
import ReactQuill, { Quill } from "react-quill-new";
import QuillTableBetter from "quill-table-better";
import "react-quill-new/dist/quill.snow.css";
import "quill-table-better/dist/quill-table-better.css";
import { TextField } from "@mui/material";
import {
  FormAutocomplete,
  formClasses,
} from "../../../../../styles/AutocompleteStyles";
import { Keys } from "../../../../../utils/Enum.keys";
import { handleMUISelectEvent } from "../../../../../utils/UtilFunctions";

if (typeof window !== "undefined" && (Quill as any).register) {
  (Quill as any).register(
    {
      "modules/table-better": QuillTableBetter,
    },
    true
  );
}

interface Props {
  setModalFlag: React.Dispatch<React.SetStateAction<idAndFlagType>>;
  idModalFlag: idAndFlagType;
  fieldType: docTypeEnum.PMU_RESPONSE | docTypeEnum.COMMENT;
}
const Edit = ({ setModalFlag, idModalFlag, fieldType }: Props) => {
  const { state } = useContext(AppContext);
  const [comment, setComment] = useState("");
  const [progress, setProgress] = useState(0);
  const { customer_id, momID, projectID } = useUrlParams();
  const [GetComment, { data }] = useLazyQuery<{ node: AtsMomDetails }, nodeVar>(
    getMomDetailsByNodeId
  );
  const [editSelectedFiles, setEditSelectedFiles] = useState<File[]>([]);
  const [message, setMessage] = useState<msgType>(emptyMessage);
  const { user_details } = useLoggedInUserDetails();
  const quillRef = useRef<ReactQuill | null>(null);
  const messageRef = useRef<HTMLTextAreaElement | null>(null);
  const [actionTaken, setActionTaken] = useState("");

  const editor = useEditor({
    extensions: [
      StarterKit,
      Document,
      Paragraph,
      Text,
      BulletList,
      OrderedList,
      ListItem,
      TextAlign.configure({
        types: ["heading", "paragraph"],
      }),
      TextStyle,
      Color,
    ],
    onUpdate: ({ editor }) => {
      // Get HTML content and store in state
      setComment(editor.getHTML());
    },
    content: comment,
  });

  useEffect(() => {
    if (idModalFlag.id) {
      GetComment({
        variables: {
          id: idModalFlag.id,
        },
      }).then(({ data }) => {
        if (data && data.node) {
          if (state.empSubType === empType.VENDOR_MANAGER) {
            setComment(data.node.vendorResponse);
            editor?.commands.setContent(data.node.vendorResponse);
          }
          if (state.empSubType === empType.PMU) {
            if (fieldType === docTypeEnum.PMU_RESPONSE) {
              setComment(data.node.pmuResponse);
              editor?.commands.setContent(data.node.pmuResponse);
            } else {
              setComment(data.node.pmuComments);
              editor?.commands.setContent(data.node.pmuComments);
            }
            setActionTaken(data.node.actionTaken);
          }
        }
      });
    }
  }, [idModalFlag.id, GetComment, data, editor]);

  const [UpdatePmu] = useMutation<
    UpdatePmuCommentsResult,
    UpdatePmuCommentsVars
  >(UpdatePmuComments, {
    onError: (e) => {
      setMessage({
        flag: true,
        message: e.message,
        operation: Operation.NONE,
      });
    },
  });

  const [UpdateResponse] = useMutation<
    UpdateVendorResponseResult,
    UpdateVendorResponseVars
  >(UpdateVendorResponse, {
    onError: (e) => {
      setMessage({
        flag: true,
        message: e.message,
        operation: Operation.NONE,
      });
    },
  });

  const [Update] = useMutation<UpdatePmuResponseResult, UpdatePmuResponseVars>(
    UpdatePmuResponse,
    {
      onError: (e) => {
        setMessage({
          flag: true,
          message: e.message,
          operation: Operation.NONE,
        });
      },
    }
  );

  const handleClear = () => {
    setComment("");
    editor?.commands.clearContent();
    setEditSelectedFiles([]);
  };

  const handleSave = () => {
    if (customer_id && momID && projectID) {
      let filename = "";
      if (editSelectedFiles) {
        filename = editSelectedFiles
          .map((file, index) => {
            return `${customer_id}/momDetails/${momID}/${projectID}/${file.name}`;
          })
          .join(";");
      }
      if (state.empSubType === empType.PMU) {
        if (fieldType === docTypeEnum.COMMENT) {
          UpdatePmu({
            variables: {
              customerID: customer_id!,
              user_details,
              momDetailsID: idModalFlag.id,
              input: {
                pmuComments: comment,
                pmuDocuments: filename,
                actionTaken: actionTaken
              },
            },
            refetchQueries: [
              {
                query: GetMomDetails,
                variables: {
                  customerID: customer_id,
                  momID,
                  projectID,
                  latest: true,
                },
              },
              {
                query: GetProjectsForMom,
                variables: {
                  customerID: customer_id,
                  empID: user_details.id,
                  momID,
                  latest: true,
                },
              },
            ],
          }).then(({ data }) => {
            if (data && data.UpdatePmuComments) {
              if (editSelectedFiles) {
                const uploadFilePromise = Promise.all(
                  editSelectedFiles.map((file, index) => {
                    const fileExt = file.name.split(".").pop();
                    handleUploadAndDownloadFile(
                      file,
                      `${customer_id}/momDetails/${momID}/${projectID}/${idModalFlag.id}_${index}.${fileExt}`,
                      setProgress,
                      false
                    );
                  })
                );

                Promise.all([uploadFilePromise]).then(([sendMsgData]) => {
                  if (sendMsgData) {
                    handleClear();
                    setMessage({
                      flag: true,
                      message: "Comment Updated successfully",
                      operation: Operation.CREATE,
                    });
                  }
                });
              }
            }
          });
        }
        if (fieldType === docTypeEnum.PMU_RESPONSE) {
          Update({
            variables: {
              customerID: customer_id!,
              user_details,
              momDetailsID: idModalFlag.id,
              input: {
                pmuResponse: comment,
                pmuResponseDocuments: filename,
              },
            },
            refetchQueries: [
              {
                query: GetMomDetails,
                variables: {
                  customerID: customer_id,
                  momID,
                  projectID,
                  latest: true,
                },
              },
              {
                query: GetProjectsForMom,
                variables: {
                  customerID: customer_id,
                  empID: user_details.id,
                  momID,
                  latest: true,
                },
              },
            ],
          }).then(({ data }) => {
            if (data && data.UpdatePmuResponse) {
              if (editSelectedFiles) {
                const uploadFilePromise = Promise.all(
                  editSelectedFiles.map((file, index) => {
                    const fileExt = file.name.split(".").pop();
                    handleUploadAndDownloadFile(
                      file,
                      `${customer_id}/momDetails/${momID}/${projectID}/pmuResponses/${idModalFlag.id}_${index}.${fileExt}`,
                      setProgress,
                      false
                    );
                  })
                );

                Promise.all([uploadFilePromise]).then(([sendMsgData]) => {
                  if (sendMsgData) {
                    handleClear();
                    setMessage({
                      flag: true,
                      message: "Response added successfully",
                      operation: Operation.CREATE,
                    });
                  }
                });
              }
            }
          });
        }
      }

      if (state.empSubType === empType.VENDOR_MANAGER)
        UpdateResponse({
          variables: {
            customerID: customer_id!,
            user_details,
            momID,
            projectID,
            input: [
              {
                vendorDocuments: filename,
                momDetailsID: idModalFlag.id,
                vendorResponse: comment,
              },
            ],
          },
          refetchQueries: [
            {
              query: GetMomDetails,
              variables: {
                customerID: customer_id,
                momID,
                projectID,
                latest: true,
              },
            },
            {
              query: GetProjectsForMom,
              variables: {
                customerID: customer_id,
                empID: user_details.id,
                momID,
                latest: true,
              },
            },
          ],
        }).then(({ data }) => {
          if (data && data.UpdateVendorResponse) {
            if (editSelectedFiles) {
              const uploadFilePromise = Promise.all(
                editSelectedFiles.map((file, index) => {
                  const fileExt = file.name.split(".").pop();
                  handleUploadAndDownloadFile(
                    file,
                    `${customer_id}/momDetails/${momID}/${projectID}/vendorResponses/${idModalFlag.id}_${index}.${fileExt}`,
                    setProgress,
                    false
                  );
                })
              );

              Promise.all([uploadFilePromise]).then(([sendMsgData]) => {
                if (sendMsgData) {
                  handleClear();
                  setMessage({
                    flag: true,
                    message: "Response updated successfully",
                    operation: Operation.CREATE,
                  });
                }
              });
            }
          }
        });
    }
  };

  const handleClose = () => {
    if (message.flag && message.operation !== Operation.NONE) {
      handleClear();
    }
    setMessage(emptyMessage);
  };

  useEffect(() => {
    const fetchData = async () => {
      if (
        data &&
        data.node.pmuDocuments &&
        state.empSubType === empType.PMU &&
        fieldType === docTypeEnum.COMMENT
      ) {
        const files = data.node.pmuDocuments.split(";");

        let index = 0;
        for (const f of files) {
          const ext = f.split(".").pop();

          const filename = `${customer_id}/momDetails/${momID}/${projectID}/${data.node.id}_${index}.${ext}`;
          const ers = await getDownloadUrl(filename, false);

          const blob = await axios
            .get(ers, { responseType: "blob" })
            .then((response) => {
              return new File([response.data], `attachement_${index}.${ext}`, {
                type: response.data.type,
              });
            });

          setEditSelectedFiles((prev) => [...prev, blob]);
          index++;
        }
      }
      if (
        data &&
        data.node.pmuResponseDocuments &&
        state.empSubType === empType.PMU &&
        fieldType === docTypeEnum.PMU_RESPONSE
      ) {
        const files = data.node.pmuResponseDocuments.split(";");

        let index = 0;
        for (const f of files) {
          const ext = f.split(".").pop();

          const filename = `${customer_id}/momDetails/${momID}/${projectID}/pmuResponses/${data.node.id}_${index}.${ext}`;
          const ers = await getDownloadUrl(filename, false);

          const blob = await axios
            .get(ers, { responseType: "blob" })
            .then((response) => {
              return new File([response.data], `attachement_${index}.${ext}`, {
                type: response.data.type,
              });
            });

          setEditSelectedFiles((prev) => [...prev, blob]);
          index++;
        }
      }

      if (
        data &&
        data.node.vendorDocuments &&
        state.empSubType === empType.VENDOR_MANAGER
      ) {
        const files = data.node.vendorDocuments.split(";");

        let index = 0;
        for (const f of files) {
          const ext = f.split(".").pop();

          const filename = `${customer_id}/momDetails/${momID}/${projectID}/vendorResponses/${data.node.id}_${index}.${ext}`;
          const ers = await getDownloadUrl(filename, false);

          const blob = await axios
            .get(ers, { responseType: "blob" })
            .then((response) => {
              return new File([response.data], `attachement_${index}.${ext}`, {
                type: response.data.type,
              });
            });

          setEditSelectedFiles((prev) => [...prev, blob]);
          index++;
        }
      }
    };
    fetchData();
  }, [data, state.empSubType]);

  const handleTextAreaChange = (
    value: string,
    delta: any,
    source: any,
    editor: ReactQuill.UnprivilegedEditor
  ): void => {
    setComment(value);
  };

  const modules = useMemo(
    () => ({
      toolbar: {
        container: [
          [{ header: 1 }, { header: 2 }], // custom button values
          ["bold", "italic", "underline", "strike"], // toggled buttons
          ["link", "image"],
          [{ list: "bullet" }],
          [{ indent: "-1" }, { indent: "+1" }], // outdent/indent
          [{ direction: "rtl" }], // text direction
          [{ size: ["small", false, "large", "huge"] }], // custom dropdown
          [{ color: [] }, { background: [] }], // dropdown with defaults from theme
          [{ font: [] }],
          [{ align: [] }],
          // ['tableUI'],
          ["table-better"], // Add table tool
        ],
      },
      table: false,
      "table-better": {
        language: "en_US",
        menus: [
          "column",
          "row",
          "merge",
          "table",
          "cell",
          "wrap",
          "copy",
          "delete",
        ],
        operationMenu: {
          items: {
            insertColumnRight: true,
            insertColumnLeft: true,
            insertRowUp: true,
            insertRowDown: true,
            deleteColumn: true,
            deleteRow: true,
            deleteTable: true,
          },
        },
        toolbarTable: true,
      },
      keyboard: {
        bindings: QuillTableBetter.keyboardBindings,
      },
    }),
    []
  );

  const handleTextAreaSelect = () => {
    const selectedText = window.getSelection()?.toString();
    if (selectedText && selectedText.length > 0) {
      messageRef.current?.focus();
    }
  };

  return (
    <div className="edit-project-meeting">
      <div className="edit-project-meeting__title">
        <Title>Edit Response</Title>
        <img src={Close} alt="" onClick={() => setModalFlag(emptyIdAndModal)} />
      </div>
      <div className="edit-project-meeting__block">
        <div className="edit-project-meeting__block--top">
          {state.empSubType === empType.VENDOR_MANAGER ? (
            <Label>Comment</Label>
          ) : null}
          <p
            dangerouslySetInnerHTML={{
              __html: data ? data.node.pmuComments : "",
            }}
          />
          {data && data.node.vendorResponse.length ? (
            <>
              {state.empSubType === empType.VENDOR_MANAGER ? (
                <Label>Previous Vendor Complaince</Label>
              ) : null}
              <p
                dangerouslySetInnerHTML={{
                  __html: data ? data.node.vendorResponse : "",
                }}
              />
            </>
          ) : null}
          {data && data.node.pmuResponse.length ? (
            <>
              {state.empSubType === empType.PMU ? (
                <Label>Previous Pmu Complaince</Label>
              ) : null}
              <p
                dangerouslySetInnerHTML={{
                  __html: data ? data.node.pmuResponse : "",
                }}
              />
            </>
          ) : null}
        </div>

        <div className="edit-project-meeting__block--bottom">
          <div className="edit-project-meeting__block--text-area">
            <Label>Description</Label>
            {/* <EditTiptapEditor
              commentState={{comment, setComment}}
              editor={editor}
              filesState={{editSelectedFiles, setEditSelectedFiles}}
            /> */}
            <ReactQuill
              ref={quillRef}
              theme={"snow"}
              value={comment}
              onChange={handleTextAreaChange}
              modules={modules}
              // formats={formatOptions}
              onChangeSelection={handleTextAreaSelect}
            />
          </div>
          {state.empSubType === empType.PMU && (
            <>
              <Label>Action to be Taken</Label>
              <FormAutocomplete
                className={formClasses.inputRoot}
                options={["vendor", "pmu"]}
                onKeyDown={(e) => {
                  if (e.key === Keys.ENTER) {
                    e.preventDefault();
                    handleMUISelectEvent(e);
                  }
                }}
                value={actionTaken}
                onChange={(e, newValue) => {
                  if (newValue) {
                    setActionTaken(newValue as string);
                  } else {
                    setActionTaken("");
                  }
                }}
                openOnFocus
                forcePopupIcon
                renderInput={(params) => (
                  <TextField
                    {...params}
                    fullWidth
                    className={formClasses.formControlRoot}
                  />
                )}
              />
            </>
          )}
        </div>
      </div>
      <Button mode="save" onClick={handleSave} />
      <Button mode="clear" onClick={handleClear} />
      <Button mode="cancel" onClick={() => setModalFlag(emptyIdAndModal)} />

      <MessageModal
        handleClose={handleClose}
        modalFlag={message.flag}
        operation={message.operation}
        value={message.message}
      />
    </div>
  );
};

export default Edit;
